import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Cube, EnvelopeSimple } from "@phosphor-icons/react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="flex items-center gap-2 mb-8">
          <Cube size={26} weight="bold" />
          <span className="font-heading font-black text-lg tracking-tighter">BuildTrack</span>
        </div>

        {sent ? (
          <div data-testid="forgot-success" className="border border-border p-6">
            <EnvelopeSimple size={32} weight="bold" className="mb-4" />
            <h1 className="font-heading text-2xl font-black tracking-tighter mb-2">Check your inbox</h1>
            <p className="text-sm text-muted-foreground mb-4">
              If an account exists for <span className="font-medium text-foreground">{email}</span>, we've sent a
              password reset link. The link expires in 1 hour.
            </p>
            <p className="text-xs text-muted-foreground mb-6">
              (Demo: the reset link is printed in the backend server logs.)
            </p>
            <Button asChild className="w-full rounded-none" data-testid="back-to-login">
              <Link to="/login">Back to sign in</Link>
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="forgot-form">
            <p className="label-caps mb-2">Password help</p>
            <h1 className="font-heading text-4xl font-black tracking-tighter mb-3">Forgot password</h1>
            <p className="text-sm text-muted-foreground mb-8">
              Enter your email and we'll send you a link to reset your password.
            </p>

            {error && (
              <div data-testid="forgot-error" className="mb-4 border border-destructive px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <Label htmlFor="email" className="label-caps">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              required data-testid="forgot-email-input" className="mt-2" placeholder="you@company.com" />

            <Button type="submit" disabled={loading} data-testid="forgot-submit-button" className="w-full mt-6 rounded-none h-11">
              {loading ? "Sending…" : "Send reset link"}
            </Button>

            <p className="text-sm text-muted-foreground mt-6 text-center">
              Remembered it?{" "}
              <Link to="/login" className="text-primary font-semibold underline underline-offset-4" data-testid="forgot-back-login">
                Sign in
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
